<template>
  <div class="domains-page">
    <div class="toolbar">
      <a-button type="primary" @click="onAddClick">新建域名</a-button>
      <a-button type="primary" @click="onScan">扫描目录</a-button>
      <a-input-search v-model="filter.q" placeholder="搜索域名" style="width:180px" @search="onFilterChange" />
      <a-select v-model="filter.health" placeholder="健康度" allowClear style="width:110px" @change="onFilterChange">
        <a-select-option value="healthy">健康</a-select-option>
        <a-select-option value="warning">警告</a-select-option>
        <a-select-option value="critical">危险</a-select-option>
        <a-select-option value="unknown">未检测</a-select-option>
      </a-select>
      <a-select v-model="filter.owner" placeholder="归属" allowClear style="width:100px" @change="onFilterChange">
        <a-select-option value="bound">有主</a-select-option>
        <a-select-option value="null">无主</a-select-option>
      </a-select>
      <a-select v-model="filter.active" placeholder="状态" allowClear style="width:100px" @change="onFilterChange">
        <a-select-option value="1">启用</a-select-option>
        <a-select-option value="0">停用</a-select-option>
      </a-select>
      <a-select v-model="filter.source" placeholder="来源" allowClear style="width:110px" @change="onFilterChange">
        <a-select-option value="manual">manual</a-select-option>
        <a-select-option value="pool">pool</a-select-option>
        <a-select-option value="purchase">purchase</a-select-option>
        <a-select-option value="byo">byo</a-select-option>
      </a-select>
      <a-button @click="loadList">刷新</a-button>
      <a-button :loading="backfilling" @click="onBackfillAll">回填 CF Zone</a-button>
      <a-button @click="onExportCsv">导出 CSV</a-button>
    </div>

    <div v-if="selectedRowKeys.length" class="batch-bar">
      <span class="batch-info">已选 {{ selectedRowKeys.length }} 个</span>
      <a-button size="small" @click="batchCheck">批量检测</a-button>
      <a-button size="small" :loading="batchPurging" @click="batchPurge">批量刷缓存</a-button>
      <a-popconfirm
        :title="`确认彻底删除选中的 ${selectedRowKeys.length} 个域名？将同时删除 CF zone、宝塔站点、数据库与缓存，不可恢复！`"
        ok-text="彻底删除" @confirm="batchDelete">
        <a-button size="small" type="danger" :loading="batchDeleting">批量删除</a-button>
      </a-popconfirm>
      <a @click="selectedRowKeys = []" style="margin-left:8px">取消选择</a>
    </div>

    <a-table :columns="columns" :data-source="items" :pagination="pagination" :loading="loading" rowKey="id"
             :row-selection="{ selectedRowKeys, onChange: (keys) => (selectedRowKeys = keys) }"
             :scroll="{ x: 1180 }"
             :locale="tableLocale"
             @change="onTableChange">
      <template slot="domain" slot-scope="text">
        <a-tooltip :title="text"><span class="domain-cell">{{ text }}</span></a-tooltip>
      </template>
      <template slot="source" slot-scope="text">
        <span class="nowrap-cell">{{ sourceLabel(text) }}</span>
      </template>
      <template slot="health" slot-scope="text"><health-tag :status="text" /></template>
      <template slot="expiry" slot-scope="text"><expiry-cell :ts="text" /></template>
      <template slot="cfzone" slot-scope="text">
        <a-tooltip v-if="text" :title="text"><span class="cf-zone-cell">{{ text }}</span></a-tooltip>
        <span v-else class="text-muted">(未托管)</span>
      </template>
      <template slot="lastCheck" slot-scope="text">
        <span v-if="text" class="nowrap-cell">{{ relTime(text) }}</span>
        <span v-else class="text-muted">—</span>
      </template>
      <template slot="action" slot-scope="row">
        <span class="action-cell">
          <a @click="openDetail(row)">详情</a>
          <a-divider type="vertical" />
          <a @click="refresh(row)">检测</a>
          <a-divider type="vertical" />
          <a @click="purgeOne(row)">刷缓存</a>
          <a-divider type="vertical" />
          <a-popconfirm
            :title="`彻底删除 ${row.domain}？将删除 CF zone、宝塔站点、数据库与缓存，不可恢复！`"
            ok-text="彻底删除" @confirm="deleteOne(row)">
            <a class="danger-link">删除</a>
          </a-popconfirm>
        </span>
      </template>
    </a-table>

    <a-modal v-model="scan.visible" title="扫描结果" :width="600" @ok="onImport"
             :okText="`导入 ${scan.unimported.length} 个`"
             :okButtonProps="{ props: { disabled: !scan.unimported.length } }">
      <p>未入库（{{ scan.unimported.length }}）：</p>
      <a-tag v-for="d in scan.unimported" :key="'u-' + d">{{ d }}</a-tag>
      <p style="margin-top:16px">库内多余（目录已不存在）（{{ scan.extra_in_db.length }}）：</p>
      <a-tag color="orange" v-for="d in scan.extra_in_db" :key="'e-' + d">{{ d }}</a-tag>
    </a-modal>

    <a-modal v-model="addForm.visible" title="新建域名" :footer="null" :width="600">
      <template v-if="!addForm.result">
        <a-form-model layout="vertical">
          <a-form-model-item label="域名（如 example.com）">
            <a-input v-model="addForm.domain" placeholder="example.com" :disabled="addForm.submitting" />
          </a-form-model-item>
          <a-form-model-item label="Owner user_id（可选，留空=未绑定）">
            <a-input v-model="addForm.owner_user_id" placeholder="数字" :disabled="addForm.submitting" />
          </a-form-model-item>
          <a-button type="primary" :loading="addForm.submitting" @click="submitAdd">提交（建宝塔站点 + 创建 CF zone）</a-button>
        </a-form-model>
      </template>
      <template v-else>
        <a-alert type="success" message="建站成功" :description="addForm.result.next_step" show-icon style="margin-bottom:16px" />
        <p><strong>请将域名 NS 改为以下 Cloudflare nameservers：</strong></p>
        <a-tag v-for="ns in (addForm.result.name_servers || [])" :key="ns" color="blue">{{ ns }}</a-tag>
        <a-divider />
        <a-button @click="pollDnsNow">立即检测 DNS 生效</a-button>
        <a-button style="margin-left:8px" @click="addForm.visible=false">关闭</a-button>
      </template>
    </a-modal>

    <detail-drawer ref="drawer" @saved="loadList" />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'
dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

import HealthTag from './components/HealthTag.vue'
import ExpiryCell from './components/ExpiryCell.vue'
import DetailDrawer from './DetailDrawer.vue'
import { listDomains, scanDomains, importDomains, checkDomain, setupDomain, checkDomainDns, deleteDomain, purgeCache, backfillZone } from '@/api/admin-domains'

export default {
  name: 'AdminDomains',
  components: { HealthTag, ExpiryCell, DetailDrawer },
  data() {
    return {
      loading: false,
      items: [],
      pagination: { current: 1, pageSize: 20, total: 0, showSizeChanger: true },
      filter: { q: '', health: undefined, owner: undefined, active: undefined, source: undefined },
      selectedRowKeys: [],
      backfilling: false,
      batchPurging: false,
      batchDeleting: false,
      scan: { visible: false, unimported: [], extra_in_db: [] },
      addForm: { visible: false, domain: '', owner_user_id: '', submitting: false, result: null },
      columns: [
        { title: '域名', dataIndex: 'domain', width: 200, scopedSlots: { customRender: 'domain' } },
        { title: '来源', dataIndex: 'source', width: 110, scopedSlots: { customRender: 'source' } },
        { title: '健康', dataIndex: 'health_overall', width: 90, scopedSlots: { customRender: 'health' } },
        { title: '注册到期', dataIndex: 'registrar_expires_at', width: 120,
          sorter: (a, b) => (a.registrar_expires_at || 0) - (b.registrar_expires_at || 0),
          scopedSlots: { customRender: 'expiry' } },
        { title: 'Owner', dataIndex: 'owner_username', width: 110, customRender: t => t || '(未绑定)' },
        { title: 'CF Zone', dataIndex: 'cf_zone_id', width: 180, scopedSlots: { customRender: 'cfzone' } },
        { title: '最近检测', dataIndex: 'last_check_at', width: 100, scopedSlots: { customRender: 'lastCheck' } },
        { title: '操作', key: 'action', width: 210, fixed: 'right', scopedSlots: { customRender: 'action' } },
      ],
    }
  },
  computed: {
    tableLocale() {
      const h = this.$createElement
      return {
        emptyText: () => h('div', { class: 'aeg-empty' }, [
          h('a-icon', { props: { type: 'global' }, class: 'aeg-empty-icon' }),
          h('p', { class: 'aeg-empty-title' }, '暂无域名'),
          h('p', { class: 'aeg-empty-desc' }, '点击左上角「新建域名」或「扫描目录」导入域名'),
        ]),
      }
    },
  },
  mounted() { this.loadList() },
  methods: {
    relTime(ts) { return dayjs(ts * 1000).fromNow() },
    sourceLabel(s) {
      const map = { manual: '手动录入', pool: '域名池', purchase: '采购', byo: '自带域名', cf_zone: 'CF 同步', cf: 'CF 同步' }
      return map[s] || (s || '—')
    },
    async loadList() {
      this.loading = true
      try {
        const r = await listDomains(this.buildParams())
        // httpClient 已解包到 envelope（{status,message,data}），业务数据在 r.data
        const root = r && r.data ? r.data : r
        this.items = (root && root.items) || []
        this.pagination.total = (root && root.total) || 0
      } catch (e) {
        this.$message.error('加载失败: ' + ((e && e.message) || e))
      } finally { this.loading = false }
    },
    buildParams() {
      const p = {
        page: this.pagination.current,
        size: this.pagination.pageSize,
        q: this.filter.q || '',
        health: this.filter.health || '',
      }
      if (this.filter.owner) p.owner = this.filter.owner
      if (this.filter.active !== undefined && this.filter.active !== null && this.filter.active !== '') p.active = this.filter.active
      if (this.filter.source) p.source = this.filter.source
      return p
    },
    onFilterChange() {
      this.pagination.current = 1
      this.selectedRowKeys = []
      this.loadList()
    },
    onTableChange(p) {
      this.pagination.current = p.current
      this.pagination.pageSize = p.pageSize
      this.loadList()
    },
    async onScan() {
      try {
        const r = await scanDomains()
        const root = r && r.data ? r.data : r
        this.scan.unimported = (root && root.unimported) || []
        this.scan.extra_in_db = (root && root.extra_in_db) || []
        this.scan.visible = true
      } catch (e) {
        this.$message.error('扫描失败: ' + ((e && e.message) || e))
      }
    },
    async onImport() {
      if (!this.scan.unimported.length) return
      // 后端限 100/批 → 前端分批 80 一组循环
      const BATCH = 80
      const all = this.scan.unimported.slice()
      let inserted = 0, skipped = 0, errs = 0
      const total_batches = Math.ceil(all.length / BATCH)
      try {
        for (let i = 0; i < total_batches; i++) {
          const slice = all.slice(i * BATCH, (i + 1) * BATCH)
          this.$message.loading({ content: `导入中... ${i + 1}/${total_batches}`, key: 'imp', duration: 0 })
          const r = await importDomains(slice)
          const root = r && r.data ? r.data : r
          inserted += (root && root.inserted) || 0
          skipped += (root && root.skipped) || 0
          errs += (root && root.errors && root.errors.length) || 0
        }
        this.$message.success({ content: `导入 ${inserted}，跳过 ${skipped}，失败 ${errs}`, key: 'imp' })
        this.scan.visible = false
        this.loadList()
      } catch (e) {
        this.$message.error({ content: '导入失败（已完成 ' + inserted + ' 个）: ' + ((e && e.message) || e), key: 'imp' })
      }
    },
    openDetail(row) { this.$refs.drawer.open(row) },
    async refresh(row) {
      this.$message.loading({ content: '检测中...', key: 'r' })
      try {
        await checkDomain(row.id, ['all'])
        this.$message.success({ content: '检测完成', key: 'r' })
        this.loadList()
      } catch (e) {
        this.$message.error({ content: '检测失败: ' + ((e && e.message) || e), key: 'r' })
      }
    },
    async purgeOne(row) {
      this.$message.loading({ content: '刷新缓存中...', key: 'p' })
      try {
        const r = await purgeCache(row.id)
        const root = r && r.data ? r.data : r
        if (root && root.success > 0) this.$message.success({ content: '已提交刷新缓存', key: 'p' })
        else {
          const first = root && root.results && root.results[0]
          this.$message.warning({ content: '刷新失败: ' + ((first && first.error) || 'unknown'), key: 'p' })
        }
      } catch (e) {
        this.$message.error({ content: '刷新失败: ' + ((e && e.message) || e), key: 'p' })
      }
    },
    async deleteOne(row) {
      this.$message.loading({ content: '删除中...', key: 'd' })
      try {
        const r = await deleteDomain(row.id, true)
        const root = r && r.data ? r.data : r
        this.reportDelete(root, row.domain)
        this.loadList()
      } catch (e) {
        this.$message.error({ content: '删除失败: ' + ((e && e.message) || e), key: 'd' })
      }
    },
    reportDelete(root, domain) {
      if (root && root.ok) { this.$message.success({ content: `已彻底删除 ${domain}`, key: 'd' }); return }
      const steps = (root && root.steps) || {}
      const failed = Object.keys(steps).filter(k => !steps[k].ok)
      if (failed.length) this.$message.warning({ content: `${domain} 已从库删除，但 ${failed.join('、')} 未清理干净，请手动处理`, key: 'd', duration: 6 })
      else this.$message.success({ content: `已删除 ${domain}`, key: 'd' })
    },
    async batchCheck() {
      const ids = [...this.selectedRowKeys]
      this.$message.info(`开始检测 ${ids.length} 个域名...`)
      await Promise.all(ids.map(id => checkDomain(id, ['dns', 'cert', 'vt']).catch(() => null)))
      this.$message.success('批量检测完成')
      this.loadList()
    },
    async batchPurge() {
      this.batchPurging = true
      try {
        const r = await purgeCache([...this.selectedRowKeys])
        const root = r && r.data ? r.data : r
        this.$message.success(`刷新缓存：成功 ${root ? root.success : 0}/${root ? root.total : 0}`)
      } catch (e) {
        this.$message.error('批量刷新失败: ' + ((e && e.message) || e))
      } finally { this.batchPurging = false }
    },
    async batchDelete() {
      const ids = [...this.selectedRowKeys]
      this.batchDeleting = true
      try {
        const arr = await Promise.all(ids.map(id => deleteDomain(id, true).then(r => (r && r.data ? r.data : r)).catch(() => ({ ok: false }))))
        const ok = arr.filter(x => x && x.ok).length
        if (ok === ids.length) this.$message.success(`已彻底删除 ${ok} 个域名`)
        else this.$message.warning(`删除 ${ok}/${ids.length} 个，部分域名 CF/宝塔 可能未清理干净`)
        this.selectedRowKeys = []
        this.loadList()
      } finally { this.batchDeleting = false }
    },
    async onBackfillAll() {
      this.backfilling = true
      try {
        const r = await backfillZone()
        const root = r && r.data ? r.data : r
        this.$message.success(`回填完成：更新 ${root ? root.updated : 0}，未找到 ${root ? root.missing : 0}`)
        this.loadList()
      } catch (e) {
        this.$message.error('回填失败: ' + ((e && e.message) || e))
      } finally { this.backfilling = false }
    },
    async onExportCsv() {
      this.$message.info('正在生成 CSV...')
      try {
        const r = await listDomains({ ...this.buildParams(), page: 1, size: 200 })
        const root = r && r.data ? r.data : r
        const items = (root && root.items) || []
        const header = ['域名', '健康度', '归属', '来源', 'CF_Zone', '状态', '注册到期', '最近检测', '备注']
        const fmt = ts => ts ? dayjs(ts * 1000).format('YYYY-MM-DD HH:mm') : ''
        const hl = { healthy: '健康', warning: '警告', critical: '危险' }
        const lines = items.map(x => [
          x.domain, hl[x.health_overall] || '未知', x.owner_username || '', x.source || '',
          x.cf_zone_id || '', x.active ? '启用' : '停用', fmt(x.registrar_expires_at),
          fmt(x.last_check_at), (x.remark || '').replace(/[\r\n,]/g, ' '),
        ].map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
        const csv = '﻿' + [header.join(','), ...lines].join('\r\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `domains_${dayjs().format('YYYYMMDD_HHmm')}.csv`
        a.click()
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('导出失败: ' + ((e && e.message) || e))
      }
    },
    onAddClick() {
      this.addForm = { visible: true, domain: '', owner_user_id: '', submitting: false, result: null }
    },
    async submitAdd() {
      const d = (this.addForm.domain || '').trim().toLowerCase()
      if (!d) { this.$message.warning('请输入域名'); return }
      this.addForm.submitting = true
      try {
        const r = await setupDomain(d, this.addForm.owner_user_id || null)
        const root = r && r.data ? r.data : r
        this.addForm.result = root
        this.$message.success('建站完成，等待 DNS 生效')
        this.loadList()
      } catch (e) {
        this.$message.error('失败: ' + ((e && e.message) || e))
      } finally {
        this.addForm.submitting = false
      }
    },
    async pollDnsNow() {
      if (!this.addForm.result || !this.addForm.result.domain_id) return
      try {
        const r = await checkDomainDns(this.addForm.result.domain_id)
        const root = r && r.data ? r.data : r
        if (root.active) {
          this.$message.success('DNS 已生效')
          this.addForm.visible = false
          this.loadList()
        } else {
          this.$message.info('DNS 尚未生效，CF 状态: ' + root.cf_status)
        }
      } catch (e) {
        this.$message.error('查询失败')
      }
    },
  },
}
</script>

<style scoped>
.domains-page { padding: 24px; background: #fff; }
.toolbar { display: flex; gap: 12px; margin-bottom: 16px; align-items: center; flex-wrap: wrap; }
.batch-bar { display: flex; gap: 8px; align-items: center; margin-bottom: 12px; padding: 8px 12px; background: #f5f7fa; border: 1px solid #e8e8e8; border-radius: 4px; }
.batch-info { color: #555; margin-right: 4px; }
.danger-link { color: #ff4d4f; }
.text-muted { color: #999; }
.domain-cell, .cf-zone-cell {
  display: inline-block; max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  vertical-align: middle;
}
.cf-zone-cell { font-family: 'SFMono-Regular', Menlo, monospace; font-size: 12px; color: #666; }
.nowrap-cell { white-space: nowrap; }
.action-cell { white-space: nowrap; }
.action-cell a { white-space: nowrap; }
.aeg-empty { padding: 32px 16px; text-align: center; color: #8c8c8c; }
.aeg-empty-icon { font-size: 40px; color: #bfbfbf; margin-bottom: 12px; }
.aeg-empty-title { margin: 0 0 4px; font-size: 15px; font-weight: 600; color: #595959; }
.aeg-empty-desc { margin: 0; font-size: 13px; color: #8c8c8c; }
</style>
